import { useState, useEffect, useRef } from "react";
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { onAuthStateChanged, signOut, User } from "firebase/auth";
import { db, storage, auth } from "./firebase/config";
import Login from "./Login";
import "./App.css";

const CATEGORIES = [
  { id: "buenos_dias", label: "Buenos Días ☀️" },
  { id: "buenas_noches", label: "Buenas Noches 🌙" },
  { id: "religiosas", label: "Religiosas 🙏" },
  { id: "naturaleza", label: "Naturaleza 🌸" },
  { id: "estaciones", label: "Festivos 🎉" },
];

const MAX_FILE_MB = 25;

// Shared images travel into WhatsApp groups full of people who don't have the
// app, so every one of them is a chance to be found — which is why the mark
// names the store rather than just the app.
//
// The exact same PNG is composited by the backfill Cloud Function, so images
// marked at upload and images marked later can't drift apart. Geometry below
// must stay in step with WM_WIDTH_FRAC / WM_PAD_FRAC in functions/index.js.
const WATERMARK_SRC = "/watermark.png";
const WM_WIDTH_FRAC = 0.30;
const WM_PAD_FRAC = 0.025;

function loadImageEl(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`could not load ${src}`));
    img.src = src;
  });
}

let watermarkPromise: Promise<HTMLImageElement> | null = null;
function loadWatermark() {
  if (!watermarkPromise) watermarkPromise = loadImageEl(WATERMARK_SRC);
  return watermarkPromise;
}

// Resize + re-encode to optimized JPEG before upload: much smaller files,
// faster loading for users, and far lower Storage bandwidth cost.
async function compressImage(file: File, maxDim = 1080, quality = 0.82): Promise<Blob> {
  const objUrl = URL.createObjectURL(file);
  let img: HTMLImageElement, mark: HTMLImageElement;
  try {
    // Rejects rather than uploading unmarked: an image that silently misses
    // the mark looks fine but quietly breaks the whole point of having one.
    [img, mark] = await Promise.all([loadImageEl(objUrl), loadWatermark()]);
  } finally {
    URL.revokeObjectURL(objUrl);
  }

  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no canvas context");
  ctx.drawImage(img, 0, 0, w, h);

  const mw = Math.round(w * WM_WIDTH_FRAC);
  const mh = Math.round((mark.height / mark.width) * mw);
  const pad = Math.round(w * WM_PAD_FRAC);
  ctx.drawImage(mark, w - mw - pad, h - mh - pad, mw, mh);

  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("compression failed"))),
      "image/jpeg",
      quality
    )
  );
}

const DAYS = [
  { id: "lunes", label: "Lunes" },
  { id: "martes", label: "Martes" },
  { id: "miercoles", label: "Miércoles" },
  { id: "jueves", label: "Jueves" },
  { id: "viernes", label: "Viernes" },
  { id: "sabado", label: "Sábado" },
  { id: "domingo", label: "Domingo" },
];

// Festivos (category id 'estaciones'). Order roughly follows the calendar so
// the filter row reads naturally rather than alphabetically.
const OCCASIONS = [
  { id: "ano_nuevo", label: "Año Nuevo 🎆" },
  { id: "reyes", label: "Reyes 👑" },
  { id: "san_valentin", label: "San Valentín ❤️" },
  { id: "carnaval", label: "Carnaval 🎭" },
  { id: "mujer", label: "Día de la Mujer 💜" },
  { id: "padre", label: "Día del Padre 👔" },
  { id: "semana_santa", label: "Semana Santa ✝️" },
  { id: "pascua", label: "Pascua 🐣" },
  { id: "madre", label: "Día de la Madre 💐" },
  { id: "amistad", label: "Amistad 🤝" },
  { id: "muertos", label: "Día de Muertos 💀" },
  { id: "halloween", label: "Halloween 🎃" },
  { id: "nochebuena", label: "Nochebuena 🕯️" },
  { id: "navidad", label: "Navidad 🎄" },
  { id: "primavera", label: "Primavera 🌸" },
  { id: "verano", label: "Verano ☀️" },
  { id: "otono", label: "Otoño 🍂" },
  { id: "invierno", label: "Invierno ❄️" },
  { id: "cumpleanos", label: "Cumpleaños 🎂" },
  { id: "fin_de_semana", label: "Fin de semana 🎉" },
  { id: "felicidades", label: "Felicidades 🎊" },
];

// Which categories carry an auto-detected tag, and which field holds it.
const TAGGED_CATEGORIES: Record<string, { field: "dayOfWeek" | "occasion"; options: { id: string; label: string }[] }> = {
  buenos_dias: { field: "dayOfWeek", options: DAYS },
  estaciones: { field: "occasion", options: OCCASIONS },
};

type ImageDoc = {
  id: string;
  url: string;
  category: string;
  featuredDate?: string;
  createdAt?: string;
  // For both tags: null = analysed, nothing found; undefined = never analysed
  dayOfWeek?: string | null;
  occasion?: string | null;
  // true once the file in Storage carries the Buendía mark
  watermarked?: boolean;
};

export default function App() {
  const [images, setImages] = useState<ImageDoc[]>([]);
  const [category, setCategory] = useState("buenos_dias");
  const [featuredDate, setFeaturedDate] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [notifStatus, setNotifStatus] = useState("");
  const [sendingNotif, setSendingNotif] = useState(false);
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  const [filterDay, setFilterDay] = useState("all");
  const [backfilling, setBackfilling] = useState(false);
  // Thumbnails are square-cropped, which hides the very text we tag on —
  // so the full image needs to be one click away when checking day tags.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Set this to your deployed Cloud Function URL after running: firebase deploy --only functions
  const NOTIFY_URL = "https://sendnotificationnow-tjj636h7ma-uc.a.run.app";
  // Canonical gen2 alias — deterministic, unlike the hashed *.run.app host.
  const FN_BASE = "https://us-central1-buenos-dias-app-5333b.cloudfunctions.net";
  const TAG_DAY_URL = `${FN_BASE}/tagImageDay`;
  const BACKFILL_URL = `${FN_BASE}/backfillImageDays`;
  const WATERMARK_URL = `${FN_BASE}/backfillWatermarks`;
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!previewUrl) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPreviewUrl(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [previewUrl]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
      setAuthChecked(true);
      if (user) loadImages();
    });
    return unsub;
  }, []);

  async function loadImages() {
    const snap = await getDocs(collection(db, "images"));
    const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as ImageDoc));
    docs.sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
    setImages(docs);
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const tooBig = Array.from(files).filter(f => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooBig.length > 0) {
      setStatus(`⚠️ ${tooBig.length} archivo(s) superan ${MAX_FILE_MB}MB y no se subirán: ${tooBig.map(f => f.name).join(", ")}`);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    setUploading(true);
    setStatus("");

    for (let i = 0; i < files.length; i++) {
      const original = files[i];
      const blob = await compressImage(original);
      const name = original.name.replace(/\.[^.]+$/, "") + ".jpg";
      const storageRef = ref(storage, `images/${Date.now()}_${name}`);
      const task = uploadBytesResumable(storageRef, blob, { contentType: "image/jpeg" });

      await new Promise<void>((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          async () => {
            const url = await getDownloadURL(task.snapshot.ref);
            const docRef = await addDoc(collection(db, "images"), {
              url,
              category,
              featuredDate: featuredDate || null,
              createdAt: new Date().toISOString(),
              // compressImage() already stamped this one — tells the backfill
              // to leave it alone rather than watermarking it a second time.
              watermarked: true,
            });
            // Auto-tag the categories that carry one (weekday for Buenos Días,
            // celebration for Festivos). A failure here must not fail the
            // upload — the tag stays editable by hand.
            if (TAGGED_CATEGORIES[category]) {
              try {
                await tagImage(docRef.id, url, category);
              } catch (e) {
                /* leave untagged */
              }
            }
            resolve();
          }
        );
      });
    }

    setStatus(`✅ ${files.length} imagen(es) subida(s)`);
    setUploading(false);
    setProgress(0);
    if (fileRef.current) fileRef.current.value = "";
    await loadImages();
  }

  async function handleDelete(id: string) {
    if (!window.confirm("¿Eliminar esta imagen?")) return;
    const img = images.find((i) => i.id === id);
    try {
      await deleteDoc(doc(db, "images", id));
      if (img?.url) {
        try {
          await deleteObject(ref(storage, img.url));
        } catch (e) {
          // File may already be gone from Storage; don't block on it
        }
      }
      await loadImages();
    } catch (e: any) {
      alert("No se pudo eliminar: " + (e?.code || e?.message || e));
    }
  }

  async function handleSetFeatured(id: string) {
    const date = window.prompt("Fecha destacada (YYYY-MM-DD):", new Date().toISOString().slice(0, 10));
    if (!date) return;
    try {
      // Clear any other image already featured for that date so the new one wins
      const clash = images.filter((img) => img.featuredDate === date && img.id !== id);
      for (const img of clash) {
        await updateDoc(doc(db, "images", img.id), { featuredDate: null });
      }
      await updateDoc(doc(db, "images", id), { featuredDate: date });
      await loadImages();
      alert("✅ Imagen destacada. Reabre la app en el móvil para verla.");
    } catch (e: any) {
      alert("No se pudo destacar: " + (e?.code || e?.message || e));
    }
  }

  // Asks the Cloud Function to read the tag off the image; the function writes
  // the field itself, so the doc is updated even though we ignore the result.
  async function tagImage(imageId: string, imageUrl: string, cat: string) {
    const token = await auth.currentUser?.getIdToken();
    const res = await fetch(TAG_DAY_URL, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ imageId, imageUrl, category: cat }),
    });
    if (!res.ok) throw new Error(`tagImage ${res.status}`);
    return (await res.json()).value as string | null;
  }

  // Changing category must clear the tag filter: a leftover value like
  // "martes" is meaningless under Festivos and would empty the grid.
  function selectCategory(id: string) {
    setFilterCategory(id);
    setFilterDay("all");
  }

  async function handleSetTag(id: string, field: string, value: string) {
    // "" stores null: analysed, and nothing found on the artwork.
    await updateDoc(doc(db, "images", id), { [field]: value || null });
    await loadImages();
  }

  async function handleBackfill() {
    // Backfill just the category in view, or everything taggable from "Todas".
    const targets = TAGGED_CATEGORIES[filterCategory]
      ? [filterCategory]
      : Object.keys(TAGGED_CATEGORIES);
    const what = targets.length === 1 ? "esta categoría" : "Buenos Días y Festivos";
    if (!window.confirm(`¿Analizar las imágenes de ${what} que aún no tienen etiqueta?`)) return;
    setBackfilling(true);
    setNotifStatus("");
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(BACKFILL_URL, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ categories: targets }),
      });
      const data = await res.json();
      setNotifStatus(data.success ? `✅ ${data.message}` : "❌ Error: " + data.error);
      await loadImages();
    } catch (e: any) {
      setNotifStatus("❌ Error: " + e.message);
    } finally {
      setBackfilling(false);
    }
  }

  // imageIds omitted = every unmarked image. Watermarking replaces the file in
  // Storage with no undo, so the UI always names how many are affected.
  async function runWatermark(imageIds: string[] | null, label: string) {
    if (!window.confirm(
      `¿Añadir la marca “Buendía” a ${label}?\n\n` +
      "Las imágenes se reemplazan en el servidor. No se puede deshacer."
    )) return;
    setBackfilling(true);
    setNotifStatus("");
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(WATERMARK_URL, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(imageIds ? { imageIds } : {}),
      });
      const data = await res.json();
      setNotifStatus(data.success ? `✅ ${data.message}` : "❌ Error: " + data.error);
      await loadImages();
    } catch (e: any) {
      setNotifStatus("❌ Error: " + e.message);
    } finally {
      setBackfilling(false);
    }
  }

  async function handleSendNotification() {
    setSendingNotif(true);
    setNotifStatus("");
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(NOTIFY_URL, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setNotifStatus(data.success ? "✅ Notificación enviada a todos los usuarios" : "❌ Error: " + data.error);
    } catch (e: any) {
      setNotifStatus("❌ Error: " + e.message);
    } finally {
      setSendingNotif(false);
    }
  }

  async function handleChangeCategory(id: string, newCategory: string) {
    await updateDoc(doc(db, "images", id), { category: newCategory });
    await loadImages();
  }

  if (!authChecked) return <div className="admin-app" />;
  if (!authUser) return <Login />;

  const byCategory = images.filter(
    (img) => filterCategory === "all" || img.category === filterCategory
  );
  // The tag filter only applies inside a category that carries a tag.
  const tagging = TAGGED_CATEGORIES[filterCategory];
  const tagField = tagging?.field;
  // "pending" (never analysed) and "none" (analysed, nothing found) look the
  // same in the grid but mean very different things.
  const matchesTag = (img: ImageDoc) => {
    if (!tagField || filterDay === "all") return true;
    const v = img[tagField];
    if (filterDay === "pending") return v === undefined;
    if (filterDay === "none") return v === null;
    return v === filterDay;
  };
  const visibleImages = tagging ? byCategory.filter(matchesTag) : byCategory;
  const unmarkedVisible = visibleImages.filter((i) => i.watermarked !== true);

  return (
    <div className="admin-app">
      <header className="admin-header">
        <div>
          <h1>🌸 Panel de Administración</h1>
          <p>Gestiona las imágenes de la app</p>
        </div>
        <div className="notif-box">
          <div className="notif-actions">
            <button className="notif-button" onClick={handleSendNotification} disabled={sendingNotif}>
              {sendingNotif ? "Enviando..." : "🔔 Enviar notificación ahora"}
            </button>
            <button className="logout-button" onClick={() => signOut(auth)}>Cerrar sesión</button>
          </div>
          {notifStatus && <p className="notif-status">{notifStatus}</p>}
        </div>
      </header>

      <section className="upload-box">
        <h2>Subir imágenes</h2>
        <div className="form-row">
          <label>Categoría:</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <label>Imagen del día (opcional):</label>
          <input type="date" value={featuredDate} onChange={(e) => setFeaturedDate(e.target.value)} />
        </div>
        <div className="form-row">
          <label style={{ color: "#aaa", fontSize: 12 }}>Las imágenes se optimizan automáticamente al subirlas</label>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} disabled={uploading} />
        </div>
        {uploading && <progress value={progress} max={100} style={{ width: "100%" }} />}
        {status && <p className="upload-status">{status}</p>}
      </section>

      <section className="gallery">
        <div className="gallery-header">
          <h2>Imágenes ({visibleImages.length})</h2>
          <button
            className="backfill-button"
            onClick={handleBackfill}
            disabled={backfilling}
            title="Analiza las imágenes de Buenos Días que aún no tienen día detectado"
          >
            {backfilling ? "Analizando..." : "🔍 Detectar etiquetas"}
          </button>
          {/* Scoped to what's on screen, so the category/day filters double as
              the selection mechanism for a trial run. */}
          <button
            className="backfill-button"
            onClick={() =>
              runWatermark(
                unmarkedVisible.map((i) => i.id),
                `las ${unmarkedVisible.length} imágenes sin marcar que se ven ahora`
              )
            }
            disabled={backfilling || unmarkedVisible.length === 0}
            title="Marca solo las imágenes visibles con los filtros actuales"
          >
            {backfilling ? "Procesando..." : `💧 Marcar visibles (${unmarkedVisible.length})`}
          </button>
        </div>
        <div className="filter-toggle">
          <button
            className={"filter-tab" + (filterCategory === "all" ? " active" : "")}
            onClick={() => selectCategory("all")}
          >
            Todas ({images.length})
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              className={"filter-tab" + (filterCategory === c.id ? " active" : "")}
              onClick={() => selectCategory(c.id)}
            >
              {c.label} ({images.filter((img) => img.category === c.id).length})
            </button>
          ))}
        </div>
        {tagging && tagField && (
          <div className="filter-toggle day-filter">
            {[{ id: "all", label: `Todos (${byCategory.length})` },
              // Hide options with no images so the Festivos row doesn't show
              // 21 mostly-empty chips.
              ...tagging.options
                .map((o) => ({
                  id: o.id,
                  label: `${o.label} (${byCategory.filter((i) => i[tagField] === o.id).length})`,
                  count: byCategory.filter((i) => i[tagField] === o.id).length,
                }))
                .filter((o) => o.count > 0),
              { id: "none", label: `Sin etiqueta (${byCategory.filter((i) => i[tagField] === null).length})` },
              { id: "pending", label: `Sin analizar (${byCategory.filter((i) => i[tagField] === undefined).length})` },
            ].map((d) => (
              <button
                key={d.id}
                className={"filter-tab" + (filterDay === d.id ? " active" : "")}
                onClick={() => setFilterDay(d.id)}
              >
                {d.label}
              </button>
            ))}
          </div>
        )}
        <div className="image-grid">
          {visibleImages.map((img) => (
            <div key={img.id} className="image-card">
              <img
                src={img.url}
                alt=""
                className="thumb"
                onClick={() => setPreviewUrl(img.url)}
                title="Ver imagen completa"
              />
              <div className="card-info">
                <select
                  value={img.category}
                  onChange={(e) => handleChangeCategory(img.id, e.target.value)}
                  className="category-select"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
                {TAGGED_CATEGORIES[img.category] && (() => {
                  const { field, options } = TAGGED_CATEGORIES[img.category];
                  const value = img[field];
                  return (
                    <select
                      value={value || ""}
                      onChange={(e) => handleSetTag(img.id, field, e.target.value)}
                      className="category-select day-select"
                      title={
                        value === undefined
                          ? "Sin analizar todavía"
                          : "Etiqueta detectada automáticamente — puedes corregirla"
                      }
                    >
                      <option value="">
                        {value === undefined ? "Sin analizar" : "Sin etiqueta"}
                      </option>
                      {options.map((o) => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                  );
                })()}
                {img.featuredDate && <span className="featured-badge">⭐ {img.featuredDate}</span>}
              </div>
              <div className="card-actions">
                <button onClick={() => handleSetFeatured(img.id)}>⭐ Destacar</button>
                {img.watermarked === true ? (
                  <button disabled title="Ya tiene la marca Buendía">💧 Marcada</button>
                ) : (
                  <button
                    onClick={() => runWatermark([img.id], "esta imagen")}
                    disabled={backfilling}
                    title="Añadir la marca Buendía solo a esta imagen"
                  >
                    💧 Marcar
                  </button>
                )}
                <button className="danger" onClick={() => handleDelete(img.id)}>🗑 Borrar</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {previewUrl && (
        <div className="preview-overlay" onClick={() => setPreviewUrl(null)}>
          <button className="preview-close" onClick={() => setPreviewUrl(null)} aria-label="Cerrar">
            ✕
          </button>
          {/* Stop propagation so clicking the image itself doesn't close it */}
          <img src={previewUrl} alt="" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
